export type QualitasStateCode = 'CA' | 'TX';

export interface StateLiabilityConfig {
    bi: string;
    pd: string;
    uiSelectable: false;
}

export interface StateQuoteConfig {
    stateCode: QualitasStateCode;
    liability: StateLiabilityConfig;
    policyTermMonths: number;
    requireDriverAgeGender: boolean;
    requireAnnualMileage: boolean;
    /** COMP/COLL deductible values offered on quick vehicle step (Q19). */
    vehicleCompCollDeductibles: ReadonlyArray<string>;
    /** Empty = shared root URL (no /tx, /ok, /ca prefix). */
    apiPathPrefix: string;
}

export const STATE_QUOTE_CONFIGS: Record<QualitasStateCode, StateQuoteConfig> = {
    CA: {
        stateCode: 'CA',
        liability: { bi: '30/60', pd: '15', uiSelectable: false },
        policyTermMonths: 6,
        requireDriverAgeGender: false,
        requireAnnualMileage: true,
        vehicleCompCollDeductibles: ['500', '1000'],
        apiPathPrefix: '',
    },
    TX: {
        stateCode: 'TX',
        liability: { bi: '30/60', pd: '25', uiSelectable: false },
        policyTermMonths: 6,
        requireDriverAgeGender: true,
        requireAnnualMileage: false,
        vehicleCompCollDeductibles: ['500', '1000', '1500'],
        apiPathPrefix: '',
    },
};

export const ALLOWED_STATE_CODES: ReadonlyArray<QualitasStateCode> = ['CA', 'TX'];

export const ALLOWED_STATES_VALIDATION_MESSAGE =
    'Only California (CA) and Texas (TX) zip codes are allowed';

export function isAllowedState(stateCode: string | null | undefined): stateCode is QualitasStateCode {
    if (!stateCode) return false;
    const code = stateCode.toUpperCase();
    return code === 'CA' || code === 'TX';
}

export function getStateQuoteConfig(stateCode: string | null | undefined): StateQuoteConfig | null {
    if (!isAllowedState(stateCode)) return null;
    return STATE_QUOTE_CONFIGS[stateCode.toUpperCase() as QualitasStateCode];
}

/** Hidden liability defaults for quick path (no BI/PD UI). */
export function getDefaultLiabilityCoverages(stateCode: string | null | undefined): Array<{ code: string; value: string; isSelected: true }> {
    const cfg = getStateQuoteConfig(stateCode);
    const bi = cfg?.liability.bi ?? STATE_QUOTE_CONFIGS.CA.liability.bi;
    const pd = cfg?.liability.pd ?? STATE_QUOTE_CONFIGS.CA.liability.pd;
    return [
        { code: 'BI', value: bi, isSelected: true },
        { code: 'PD', value: pd, isSelected: true },
    ];
}

/** Display label for a COMP/COLL deductible value. Value itself is unchanged. */
function compCollDeductibleLabel(deductible: string): string {
    switch (deductible) {
        case '0':
            return 'Bodily Injury and Property Damage Liability';
        case '500':
            return 'Full coverage ($500 Deductible)';
        case '1000':
            return 'Full coverage ($1,000 Deductible)';
        default: {
            const amount = Number(deductible);
            const formatted = Number.isFinite(amount) ? amount.toLocaleString('en-US') : deductible;
            return `Full coverage ($${formatted} Deductible)`;
        }
    }
}

/** COMP/COLL deductible options for quick vehicle step (Q19). Value 0 = liability only. */
export function getCompCollDeductibleOptions(stateCode: string | null | undefined): ReadonlyArray<{ label: string; value: string }> {
    const deductibles = getStateQuoteConfig(stateCode)?.vehicleCompCollDeductibles
        ?? STATE_QUOTE_CONFIGS.CA.vehicleCompCollDeductibles;
    const options: Array<{ label: string; value: string }> = [
        { label: compCollDeductibleLabel('0'), value: '0' },
    ];
    deductibles.forEach((d) => {
        options.push({ label: compCollDeductibleLabel(d), value: d });
    });
    return options;
}

/**
 * Whether annual mileage is collected for the state (CA yes / TX no).
 * Default value comes from PPA zip lookup — see fetchDefaultAnnualMileageByZip.
 */
export function requiresAnnualMileage(stateCode: string | null | undefined): boolean {
    return !!getStateQuoteConfig(stateCode)?.requireAnnualMileage;
}

/** Quick-quote discounts. Good Driver is the only one, and it applies in CA. */
export function hasQuickQuoteDiscounts(stateCode: string | null | undefined): boolean {
    return stateCode === 'CA';
}

/** @deprecated Use fetchDefaultAnnualMileageByZip — kept for any leftover callers. */
export function getDefaultAnnualMileageStub(stateCode: string | null | undefined, _zipCode?: string | null): string | null {
    if (!requiresAnnualMileage(stateCode)) return null;
    return null;
}

/** Qualitas vehicle use options. Values are VehicleUse enum Alts. Farming stays off the screen. */
export const QUALITAS_VEHICLE_USE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'Personal', label: 'Pleasure' },
    { value: 'Commute', label: 'Commute (to Work/School)' },
    { value: 'Business', label: 'Business (i.e. Realtors,Accountants)' },
    { value: 'Other', label: 'Artisan (i.e. Gardener, Painter, etc.)' },
];

/**
 * Vehicle use by state (D2C 10/13/2026 Req #3):
 * TX — Business (and Artisan) removed; CA — Business kept, Artisan (`Other` → carrier `A`) added.
 */
export function getVehicleUseOptions(stateCode: string | null | undefined): ReadonlyArray<{ value: string; label: string }> {
    const code = (stateCode || '').toUpperCase();
    if (code === 'TX') {
        return QUALITAS_VEHICLE_USE_OPTIONS.filter(o => o.value !== 'Business' && o.value !== 'Other');
    }
    return QUALITAS_VEHICLE_USE_OPTIONS;
}

export const DEFAULT_VEHICLE_USE = 'Commute';

/** Req #5: privacy notice linked from the phone/email consent note on the driver step. */
export const PRIVACY_NOTICE_URL = 'https://www.qualitasinsurance.com/Home/Privacy?lang=en';

/** Req #4: shown (and Continue blocked) when a CA driver picks Expired license status. */
export const EXPIRED_LICENSE_CA_MESSAGE = 'Expired Licenses are not accepted for this program';

export const GARAGING_COUNTRY = {
    USA: 'USA',
    MEXICO: 'Mexico',
} as const;

export const GARAGING_COUNTRY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: GARAGING_COUNTRY.USA, label: 'USA' },
    { value: GARAGING_COUNTRY.MEXICO, label: 'Mexico' },
];

/** DL License State/Country combined dropdown (Q7): Non-US FOR/MC/MX + US states.
 *  Default = quote `stateCode` from starting zip (Q21), not hardcoded CA. */

const US_DL_STATE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'DC', label: 'District Of Columbia' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
];

export const DL_LICENSE_STATE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'FOR', label: 'Foreign' },
    { value: 'MC', label: 'Matricular Consular' },
    { value: 'MX', label: 'Mexico' },
    ...US_DL_STATE_OPTIONS,
];

/** Default DL state = quote state from zip (CA/TX). Fallback CA if unknown. */
export function getDefaultDlLicenseState(stateCode: string | null | undefined): string {
    const code = (stateCode || '').toUpperCase();
    if (US_DL_STATE_OPTIONS.some((x) => x.value === code)) {
        return code;
    }
    return 'CA';
}

/** @deprecated Use getDefaultDlLicenseState(quoteStateCode). */
export const DEFAULT_DL_LICENSE_STATE = 'CA';

/** Years Licensed select options (0–50). */
export const LICENSE_YEARS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = Array.from(
    { length: 51 },
    (_, i) => ({ value: String(i), label: String(i) })
);
